import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Sóng dừng trên dây 2 đầu cố định (Lớp 11 — Bài 9)
// y(x,t) = 2A·sin(nπx/L)·cos(ωt). n = số bó sóng = số bụng; số nút = n+1; λ = 2L/n.
// Nút: điểm đứng yên; Bụng: dao động biên độ cực đại.
const W = 660, H = 285, X0 = 40, X1 = 620, MID = 120, AMP = 78

const StandingWaveSimulation: React.FC<Props> = ({
  title = 'Sóng dừng trên dây hai đầu cố định',
  description = 'Hai sóng ngược chiều chồng chất tạo sóng dừng: nút (đứng yên) và bụng (dao động cực đại). Số bó n → λ = 2L/n.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const [n, setN] = useState(3)
  const [running, setRunning] = useState(true)
  const nRef = useRef(n), runRef = useRef(running)
  const firstRef = useRef(false)
  useEffect(() => { nRef.current = n }, [n])
  useEffect(() => { runRef.current = running }, [running])
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const L = X1 - X0, nn = nRef.current, t = tRef.current
    const env = (x: number) => AMP * Math.sin((nn * Math.PI * (x - X0)) / L) // biên độ tại x
    const y = (x: number) => MID - env(x) * Math.cos(t)
    // bao hình (envelope) mờ
    ctx.fillStyle = 'rgba(124,58,237,0.10)'; ctx.beginPath(); ctx.moveTo(X0, MID)
    for (let x = X0; x <= X1; x += 4) ctx.lineTo(x, MID - Math.abs(env(x)))
    for (let x = X1; x >= X0; x -= 4) ctx.lineTo(x, MID + Math.abs(env(x)))
    ctx.closePath(); ctx.fill()
    // dây
    ctx.strokeStyle = '#7c3aed'; ctx.lineWidth = 3; ctx.beginPath()
    for (let x = X0; x <= X1; x += 2) { const py = y(x); x === X0 ? ctx.moveTo(x, py) : ctx.lineTo(x, py) }
    ctx.stroke()
    // 2 đầu cố định
    ctx.fillStyle = '#1e293b'; ctx.fillRect(X0 - 6, MID - 30, 6, 60); ctx.fillRect(X1, MID - 30, 6, 60)
    // nút (đỏ) tại x = X0 + k·L/n ; bụng (cam) tại giữa các nút
    for (let k = 0; k <= nn; k++) {
      const xn = X0 + (k * L) / nn
      ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(xn, MID, 4.5, 0, Math.PI * 2); ctx.fill()
    }
    for (let k = 0; k < nn; k++) {
      const xb = X0 + ((k + 0.5) * L) / nn
      ctx.fillStyle = '#f59e0b'; ctx.beginPath(); ctx.arc(xb, y(xb), 4, 0, Math.PI * 2); ctx.fill()
    }
    // nhãn Bụng (trên 1 bụng) & Nút (dưới 1 nút)
    const xb = X0 + (0.5 * L) / nn, xn = Math.min(X1, X0 + L / nn)
    ctx.textAlign = 'center'
    ctx.fillStyle = '#b45309'; ctx.font = 'bold 12px sans-serif'; ctx.fillText('Bụng', xb, MID - AMP - 8)
    ctx.fillStyle = '#dc2626'; ctx.fillText('Nút', xn, MID + 24)
    // khoảng cách 2 nút liên tiếp = λ/2
    const a = X0, b = X0 + L / nn, yb = MID + AMP + 30
    ctx.strokeStyle = '#334155'; ctx.fillStyle = '#334155'; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(a, yb - 6); ctx.lineTo(a, yb + 6); ctx.moveTo(b, yb - 6); ctx.lineTo(b, yb + 6); ctx.moveTo(a, yb); ctx.lineTo(b, yb); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(a, yb); ctx.lineTo(a + 7, yb - 3); ctx.lineTo(a + 7, yb + 3); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(b, yb); ctx.lineTo(b - 7, yb - 3); ctx.lineTo(b - 7, yb + 3); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 13px sans-serif'; ctx.fillText('λ/2', (a + b) / 2, yb - 6)
    ctx.textAlign = 'left'
    // chú thích
    ctx.fillStyle = '#dc2626'; ctx.font = 'bold 12px sans-serif'; ctx.fillText('● nút (đứng yên)', X0, H - 30)
    ctx.fillStyle = '#f59e0b'; ctx.fillText('● bụng (cực đại)', X0 + 130, H - 30)
    ctx.fillStyle = '#334155'; ctx.font = 'bold 13px sans-serif'
    ctx.fillText(`Số bó n = ${nn}   ·   số bụng = ${nn}   ·   số nút = ${nn + 1}   ·   λ = 2L/${nn}`, X0, H - 12)
  }, [])

  const tick = useCallback(() => {
    if (runRef.current) tRef.current += 0.06
    draw(); raf.current = requestAnimationFrame(tick)
  }, [draw])
  useEffect(() => { raf.current = requestAnimationFrame(tick); return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [tick])

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 my-3">
      <h4 className="font-bold text-violet-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="flex items-center gap-3 mt-3 text-sm">
        <label className="flex items-center gap-2 flex-1"><span className="w-40 text-gray-600">Số bó sóng n (hoạ âm)</span>
          <input type="range" min={1} max={6} step={1} value={n} onChange={(e) => { touch(); setN(Number(e.target.value)) }} className="flex-1 accent-violet-500" />
          <span className="w-8 text-right font-semibold">{n}</span></label>
        <button onClick={() => { touch(); setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700">{running ? <><Pause className="w-4 h-4" /> Dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Điều kiện sóng dừng 2 đầu cố định: L = n·λ/2 (n = 1, 2, 3…). Khoảng cách 2 nút (hoặc 2 bụng) liên tiếp = λ/2.</p>
    </div>
  )
}
export default StandingWaveSimulation
